import React, { useState } from 'react'
import {
	Instagram,
	Phone,
	Mail,
	MapPin,
	Clock,
	Send,
	Shield,
	Award,
	Truck,
	ChevronRight,
} from 'lucide-react'
import * as Yup from 'yup'
import { ContactEntity, Status } from '@/entities/api/contact/contact'
import { createContact } from '@/services/api/contact/contact'
import { Link } from 'react-router-dom'

const contactSchema = Yup.object({
	fullname: Yup.string()
		.required('El nombre completo es requerido')
		.min(2, 'El nombre debe tener al menos 2 caracteres')
		.max(50, 'El nombre no puede exceder 50 caracteres'),
	phone: Yup.number()
		.required('El teléfono es requerido')
		.positive('Ingrese un número válido')
		.integer('Ingrese un número válido'),
	email: Yup.string()
		.required('El email es requerido')
		.email('Ingrese un email válido'),
	message: Yup.string()
		.required('El mensaje es requerido')
		.min(10, 'El mensaje debe tener al menos 10 caracteres')
		.max(500, 'El mensaje no puede exceder 500 caracteres'),
})

const Footer: React.FC = () => {
	const [formData, setFormData] = useState({
		fullname: '',
		phone: '',
		email: '',
		message: '',
	})
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [showSuccess, setShowSuccess] = useState(false)
	const [showError, setShowError] = useState(false)

	const handleInputChange = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
	) => {
		const { name, value } = e.target
		setFormData((prev) => ({ ...prev, [name]: value }))

		if (errors[name]) {
			setErrors((prev) => ({ ...prev, [name]: '' }))
		}
	}

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault()
		setIsSubmitting(true)

		try {
			await contactSchema.validate(formData, { abortEarly: false })

			const contactEntity: ContactEntity = {
				status: Status.pendding,
				fullname: formData.fullname,
				phone: Number(formData.phone),
				email: formData.email,
				message: formData.message,
			}

			const response = await createContact(contactEntity)

			if (response?.status && response.status >= 201) {
				setShowSuccess(true)
				setInterval(() => setShowSuccess(false), 10000)
				setFormData({ fullname: '', phone: '', email: '', message: '' })
				setErrors({})
			} else {
				setShowError(true)
				setInterval(() => setShowError(false), 10000)
			}
		} catch (error) {
			setErrors({})
			setShowError(true)
			setInterval(() => setShowError(false), 10000)
			setIsSubmitting(false)
		} finally {
			setIsSubmitting(false)
		}
	}

	const navigationLinks = [
		{ to: '/', label: 'Inicio' },
		{ to: '/Categoria/suplementos', label: 'Suplementos' },
		// { to: '/Categoria/planes-personalizados', label: 'Ropa Deportiva' },
		// { to: '/Categoria/ropa', label: 'Accesorios' },
		{ to: '/Categoria/accesorios', label: 'Planes Personalizados' },
	]

	const legalLinks = [
		{ to: '/Terminos', label: 'Términos y Condiciones' },
		{ to: '/Terminos', label: 'Política de Privacidad' },
		{ to: '/Terminos', label: 'Política de Devoluciones' },
		{ to: '/Terminos', label: 'Política de Envíos' },
	]

	return (
		<>
			<div className='w-full bg-white'>
				<svg
					className='w-full h-12 md:h-16 lg:h-20'
					xmlns='http://www.w3.org/2000/svg'
					viewBox='0 0 1440 320'
					preserveAspectRatio='none'
				>
					<defs>
						<linearGradient id='waveGradient' x1='0%' y1='0%' x2='100%' y2='0%'>
							<stop offset='0%' stopColor='#F3F4F6' />
							<stop offset='50%' stopColor='#F3F4F6' />
							<stop offset='100%' stopColor='#F3F4F6' />
						</linearGradient>
					</defs>
					<path
						fill='url(#waveGradient)'
						fillOpacity='1'
						d='M0,160L80,144C160,128,320,96,480,122.7C640,149,800,235,960,256C1120,277,1280,235,1360,213.3L1440,192L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z'
					/>
				</svg>
			</div>

			<footer className='bg-gray-100 relative overflow-hidden'>
				<div className='absolute inset-0 opacity-5'>
					<div
						className='absolute inset-0'
						style={{
							backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23dc2626' fill-opacity='0.1'%3E%3Ccircle cx='30' cy='30' r='1'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
						}}
					/>
				</div>

				<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative'>
					<div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-8 lg:gap-10 py-12 lg:py-16'>
						<div className='md:col-span-2 lg:col-span-4 space-y-8'>
							<div className='space-y-4'>
								<div className='flex items-center space-x-3'>
									<div className='w-10 h-10 rounded-lg flex items-center justify-center overflow-hidden'>
										<img
											src='/logo.png'
											alt='SportCenter Logo'
											className='w-8 h-8 object-contain'
										/>
									</div>

									<div>
										<h1 className='text-2xl xl:text-3xl font-black text-gray-900 tracking-tight'>
											<span className='text-red-600'>Sport</span>Center
										</h1>
										<p className='text-xs text-gray-500 font-medium -mt-1'>
											SHOP
										</p>
									</div>
								</div>
								<p className='text-gray-600 text-sm leading-relaxed max-w-sm'>
									Tu aliado perfecto en el mundo fitness. Especialistas en
									suplementos premium, accesorios de calidad y planes
									personalizados para transformar tu vida.
								</p>
							</div>

							<div className='grid grid-cols-2 gap-4'>
								<div className='bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50 hover:shadow-md transition-all duration-300'>
									<div className='flex items-center space-x-3'>
										<div className='w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center shadow-md'>
											<Shield className='w-5 h-5 text-white' />
										</div>
										<div>
											<p className='text-sm font-bold text-gray-800'>
												Compra Segura
											</p>
											<p className='text-xs text-gray-500'>SSL & Encriptado</p>
										</div>
									</div>
								</div>
								<div className='bg-white/70 backdrop-blur-sm rounded-xl p-4 border border-gray-200/50 hover:shadow-md transition-all duration-300'>
									<div className='flex items-center space-x-3'>
										<div className='w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center shadow-md'>
											<Award className='w-5 h-5 text-white' />
										</div>
										<div>
											<p className='text-sm font-bold text-gray-800'>
												Calidad Premium
											</p>
											<p className='text-xs text-gray-500'>Certificado</p>
										</div>
									</div>
								</div>
							</div>

							<div className='space-y-4'>
								<p className='text-sm font-bold text-gray-800'>Síguenos</p>
								<div className='flex space-x-3 gap-4'>
									{[
										{
											icon: Instagram,
											label: 'Instagram',
											action:
												'https://www.instagram.com/sportcenter_chinchina?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==',
											color: 'from-pink-500 to-purple-600',
										},
									].map(({ icon: Icon, label, color, action }) => (
										<button
											onClick={() => window.open(action, '_blank')}
											key={label}
											className={`w-12 cursor-pointer h-12 bg-gradient-to-br ${color} rounded-xl flex items-center justify-center text-white hover:scale-110 transition-all duration-300 shadow-lg hover:shadow-xl group`}
											aria-label={label}
										>
											<Icon className='w-5 h-5 group-hover:scale-110 transition-transform' />
										</button>
									))}
								</div>
							</div>
						</div>

						<div className='lg:col-span-2 space-y-6'>
							<h3 className='text-lg font-bold text-gray-900 border-b-2 border-red-600 pb-2 inline-block'>
								Navegación
							</h3>
							<ul className='space-y-3'>
								{navigationLinks.map((link) => (
									<li key={link.to}>
										<Link
											to={link.to}
											className='flex items-center text-sm text-gray-600 hover:text-red-600 transition-all duration-200 group hover:translate-x-1'
										>
											<ChevronRight className='w-4 h-4 mr-2 text-gray-400 group-hover:text-red-600 transition-colors' />
											{link.label}
										</Link>
									</li>
								))}
							</ul>
						</div>

						<div className='lg:col-span-2 space-y-6'>
							<h3 className='text-lg font-bold text-gray-900 border-b-2 border-red-600 pb-2 inline-block'>
								Legal
							</h3>
							<ul className='space-y-3'>
								{legalLinks.map((link) => (
									<li key={`${link.to}-${link.label}`}>
										<Link
											to={link.to}
											className='flex items-center text-sm text-gray-600 hover:text-red-600 transition-all duration-200 group hover:translate-x-1'
										>
											<ChevronRight className='w-4 h-4 mr-2 text-gray-400 group-hover:text-red-600 transition-colors' />
											{link.label}
										</Link>
									</li>
								))}
							</ul>
						</div>

						<div className='md:col-span-2 lg:col-span-4'>
							<div className='bg-white/80 backdrop-blur-sm rounded-2xl p-6 lg:p-8 shadow-xl border border-gray-200/50 hover:shadow-2xl transition-all duration-300'>
								<div className='mb-6'>
									<div className='flex items-center space-x-3 mb-3'>
										<div className='w-10 h-10 bg-gradient-to-br from-red-500 to-red-600 rounded-lg flex items-center justify-center'>
											<Mail className='w-5 h-5 text-white' />
										</div>
										<h3 className='text-xl font-bold text-gray-900'>
											Contáctanos
										</h3>
									</div>
									<p className='text-sm text-gray-600'>
										¿Dudas sobre nuestros productos? Escríbenos y te ayudamos.
									</p>
								</div>

								{showSuccess && (
									<div className='w-full mb-4 bg-green-500 text-white px-4 py-2 rounded-lg text-sm flex items-center space-x-2 border border-green-400'>
										<Shield className='w-4 h-4 flex-shrink-0' />
										<span>¡Mensaje enviado! Te contactaremos pronto.</span>
									</div>
								)}

								{showError && (
									<div className='w-full mb-4 bg-red-500 text-white px-4 py-2 rounded-lg text-sm flex items-center space-x-2 border border-red-400'>
										<Shield className='w-4 h-4 flex-shrink-0' />
										<span>
											¡Algo falló al enviar el mensaje! Inténtalo más tarde.
										</span>
									</div>
								)}

								<form onSubmit={handleSubmit} className='space-y-5'>
									<div>
										<label className='block text-sm font-semibold text-gray-700 mb-2'>
											Nombre Completo *
										</label>
										<input
											type='text'
											name='fullname'
											value={formData.fullname}
											onChange={handleInputChange}
											className={`w-full px-4 py-3 text-sm border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 bg-white/90 ${
												errors.fullname
													? 'border-red-300 bg-red-50/50'
													: 'border-gray-200 hover:border-gray-300'
											}`}
											placeholder='Escribe tu nombre completo'
										/>
										{errors.fullname && (
											<p className='mt-2 text-xs text-red-600 flex items-center space-x-1'>
												<span className='w-1 h-1 bg-red-600 rounded-full'></span>
												<span>{errors.fullname}</span>
											</p>
										)}
									</div>

									<div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
										<div>
											<label className='block text-sm font-semibold text-gray-700 mb-2'>
												Teléfono *
											</label>
											<input
												type='tel'
												name='phone'
												value={formData.phone}
												onChange={handleInputChange}
												className={`w-full px-4 py-3 text-sm border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 bg-white/90 ${
													errors.phone
														? 'border-red-300 bg-red-50/50'
														: 'border-gray-200 hover:border-gray-300'
												}`}
												placeholder='3001234567'
											/>
											{errors.phone && (
												<p className='mt-2 text-xs text-red-600 flex items-center space-x-1'>
													<span className='w-1 h-1 bg-red-600 rounded-full'></span>
													<span>{errors.phone}</span>
												</p>
											)}
										</div>

										<div>
											<label className='block text-sm font-semibold text-gray-700 mb-2'>
												Email *
											</label>
											<input
												type='email'
												name='email'
												value={formData.email}
												onChange={handleInputChange}
												className={`w-full px-4 py-3 text-sm border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 bg-white/90 ${
													errors.email
														? 'border-red-300 bg-red-50/50'
														: 'border-gray-200 hover:border-gray-300'
												}`}
												placeholder='tu@email.com'
											/>
											{errors.email && (
												<p className='mt-2 text-xs text-red-600 flex items-center space-x-1'>
													<span className='w-1 h-1 bg-red-600 rounded-full'></span>
													<span>{errors.email}</span>
												</p>
											)}
										</div>
									</div>

									<div>
										<label className='block text-sm font-semibold text-gray-700 mb-2'>
											Mensaje *
										</label>
										<textarea
											name='message'
											value={formData.message}
											onChange={handleInputChange}
											rows={4}
											className={`w-full px-4 py-3 text-sm border-2 rounded-xl focus:outline-none focus:ring-4 focus:ring-red-500/20 focus:border-red-500 transition-all duration-200 resize-none bg-white/90 ${
												errors.message
													? 'border-red-300 bg-red-50/50'
													: 'border-gray-200 hover:border-gray-300'
											}`}
											placeholder='Cuéntanos en qué te podemos ayudar...'
										/>
										{errors.message && (
											<p className='mt-2 text-xs text-red-600 flex items-center space-x-1'>
												<span className='w-1 h-1 bg-red-600 rounded-full'></span>
												<span>{errors.message}</span>
											</p>
										)}
									</div>

									<button
										type='submit'
										disabled={isSubmitting}
										className='w-full bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:from-red-400 disabled:to-red-500 text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 flex items-center justify-center space-x-3 hover:scale-105 hover:shadow-xl disabled:scale-100 disabled:shadow-none group overflow-hidden relative'
									>
										<div className='absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000'></div>
										{isSubmitting ? (
											<>
												<div className='w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin' />
												<span className='ml-4'>Enviando mensaje...</span>
											</>
										) : (
											<>
												<Send className='w-5 h-5 group-hover:translate-x-1 transition-transform' />
												<span className='ml-4'>Enviar Mensaje</span>
											</>
										)}
									</button>
								</form>
							</div>

							<div className='mt-6 bg-white/50 backdrop-blur-sm rounded-xl p-6 border border-gray-200/50 space-y-4'>
								{[
									{
										icon: Phone,
										text: '+57 322 5432 580',
										color: 'text-green-600',
									},
									{
										icon: Mail,
										text: 'sportcenterchinchina@gmail.com',
										color: 'text-blue-600',
									},
									{
										icon: MapPin,
										text: 'Cra. 8a, C.C. 2° piso, Chinchiná, Caldas, Colombia',
										color: 'text-purple-600',
									},
									{
										icon: Clock,
										text: 'Lun - Dom: 8:00 AM - 10:00 PM',
										color: 'text-orange-600',
									},
								].map(({ icon: Icon, text, color }) => (
									<div
										key={text}
										className='flex items-center space-x-4 p-2 rounded-lg hover:bg-white/60 transition-colors'
									>
										<div className='w-10 h-10 bg-white rounded-lg flex items-center justify-center shadow-sm'>
											<Icon className={`w-5 h-5 ${color}`} />
										</div>
										<span className='text-sm font-medium text-gray-700'>
											{text}
										</span>
									</div>
								))}
							</div>
						</div>
					</div>

					<div className='border-t border-gray-200/60 bg-white/30 backdrop-blur-sm rounded-t-2xl mx-4 sm:mx-6 lg:mx-8'>
						<div className='px-6 py-6'>
							<div className='flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0'>
								<div className='flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-4'>
									<p className='text-sm text-gray-600 font-medium'>
										© {new Date().getFullYear()} Sce Shop
									</p>
									<span className='hidden sm:block w-1 h-1 bg-gray-400 rounded-full'></span>
									<p className='text-sm text-gray-500'>
										Todos los derechos reservados
									</p>
								</div>
								<div className='flex items-center space-x-3 bg-gradient-to-r from-emerald-50 to-green-50 px-4 py-2 rounded-full border border-emerald-200'>
									<div className='w-8 h-8 bg-gradient-to-br from-emerald-500 to-green-600 rounded-full flex items-center justify-center'>
										<Truck className='w-4 h-4 text-white' />
									</div>
									<span className='text-sm text-gray-700 font-semibold'>
										Envíos gratis en toda la tienda
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</footer>
		</>
	)
}

export default Footer
